import { useState } from 'react'
import { toast } from 'react-toastify'
import { useJobRoles } from '@/api/queries/people-queries'
import { CommitmentMatrixMultiSelector } from '@/components/administration/commitment-matrix/commitment-matrix-multi-selector'
import { Button } from '@/components/ui/button'
import { Field, FieldContent, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import type { JobGroup } from '@/types'

export interface JobGroupFormValues {
	name: string
	jobRoles: string[]
}

interface JobGroupFormProps {
	mode: 'create' | 'edit'
	initial?: JobGroup
	onSave: (values: JobGroupFormValues) => void
	onClose: () => void
	isSaving?: boolean
}

export function JobGroupFormContent({
	mode,
	initial,
	onSave,
	onClose,
	isSaving = false,
}: JobGroupFormProps) {
	const [name, setName] = useState(initial?.name ?? '')
	const [jobRoles, setJobRoles] = useState<string[]>(initial?.jobRoles ?? [])

	const { data: allJobRoles, isLoading: isLoadingJobRoles } = useJobRoles()

	const jobRoleOptions = Array.from(
		new Set([...(allJobRoles ?? []), ...jobRoles]),
	).map((role) => ({ id: role, name: role }))

	const handleSave = () => {
		if (!name.trim()) {
			toast.error('Informe um nome para o grupo.')
			return
		}

		onSave({ name: name.trim(), jobRoles })
	}

	return (
		<div className="flex flex-col gap-5 px-1">
			<Field>
				<FieldLabel htmlFor="job-group-name">Nome do grupo</FieldLabel>
				<FieldContent>
					<Input
						id="job-group-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder="Ex: Operacional"
						maxLength={120}
					/>
				</FieldContent>
			</Field>

			<Separator className="bg-border/60" />

			<div className="flex flex-col gap-3">
				<p className="text-xs text-muted-foreground">
					Selecione os cargos (sincronizados do ERP) que pertencem a este grupo.
				</p>

				<CommitmentMatrixMultiSelector
					title="Cargos"
					options={jobRoleOptions}
					selected={jobRoles}
					onChange={setJobRoles}
					searchPlaceholder="Buscar cargo..."
					emptyMessage="Nenhum cargo encontrado"
					loading={isLoadingJobRoles}
					defaultOpen
				/>
			</div>

			<Separator className="bg-border/60" />

			<div className="flex gap-3 pb-1">
				<Button
					type="button"
					variant="cancel"
					onClick={onClose}
					className="flex-1"
					disabled={isSaving}
				>
					Cancelar
				</Button>
				<Button
					type="button"
					onClick={handleSave}
					disabled={isSaving}
					className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
				>
					{isSaving
						? 'Salvando...'
						: mode === 'create'
							? 'Criar grupo'
							: 'Salvar alterações'}
				</Button>
			</div>
		</div>
	)
}
